import React, { useState, useEffect } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { DiagnosticResult, Subject, Question, UserResponse } from '../../types';
import { DeepSeekService } from '../../services/deepseekApi';
import { API_CONFIG, DEV_CONFIG } from '../../config/api';
import { Brain, Target, TrendingUp, AlertTriangle, BookOpen, Lightbulb, Sparkles, Loader, Zap, Users, Clock } from 'lucide-react';

interface EnhancedResultsViewProps {
  result: DiagnosticResult;
  subject: Subject;
  questions: Question[];
  responses: UserResponse[];
  onRestart: () => void;
  testMode?: 'classic' | 'adaptive';
}

interface AIAnalysis {
  strengths: string[];
  weaknesses: string[];
  learningStyle: string;
  cognitiveProfile: string;
  detailedInsights: string[];
  recommendations: string[];
  nextSteps: string[];
}

export const EnhancedResultsView: React.FC<EnhancedResultsViewProps> = ({
  result,
  subject,
  questions,
  responses,
  onRestart,
  testMode = 'classic'
}) => {
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);

  const dimensionNames = {
    processing_speed: '信息处理速度',
    working_memory: '工作记忆',
    abstract_thinking: '抽象思维',
    pattern_recognition: '模式识别',
    transfer_application: '迁移应用',
    metacognitive_monitoring: '元认知监控'
  };

  // 在组件加载时进行AI分析
  useEffect(() => {
    performAIAnalysis();
  }, []);

  const performAIAnalysis = async () => {
    setIsAnalyzing(true);
    
    try {
      if (DEV_CONFIG.USE_MOCK_AI || !API_CONFIG.DEEPSEEK.API_KEY) {
        // 使用模拟分析结果
        setTimeout(() => {
          setAiAnalysis({
            strengths: ['计算基础扎实', '逻辑思维清晰', '学习态度认真'],
            weaknesses: ['答题速度需提升', '复杂问题分析能力有待加强'],
            learningStyle: '稳健型学习者，注重准确性，思考细致但速度相对较慢',
            cognitiveProfile: '具备良好的基础能力，在简单题目上表现优异，但在面对复杂问题时需要更多时间思考。表现出较强的学习意愿和自我要求。',
            detailedInsights: [
              '在基础计算题上正确率很高，说明基本功扎实',
              '答题时间普遍较长，反映出谨慎的思考习惯',
              '在抽象推理题目上有提升空间',
              '学习动机较强，但可能过于追求完美而影响效率'
            ],
            recommendations: [
              '适当增加限时练习，提高思维敏捷性',
              '多做中等难度的推理题，循序渐进提升抽象思维能力',
              '学会在准确性和速度之间找到平衡',
              '建立解题模板，提高解题效率'
            ],
            nextSteps: [
              '每天进行15分钟的限时基础练习',
              '每周安排2-3次中等难度的综合练习',
              '学习并练习快速解题技巧',
              '定期自我评估，调整学习策略'
            ]
          });
          setIsAnalyzing(false);
        }, 2000);
      } else {
        // 使用真实的DeepSeek API
        const deepseekService = new DeepSeekService(API_CONFIG.DEEPSEEK.API_KEY);
        const analysis = await deepseekService.analyzeUserPerformance(responses, questions, subject);
        setAiAnalysis(analysis);
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('AI分析失败:', error);
      setIsAnalyzing(false);
    }
  };

  // 修复双击问题的关键：使用useCallback和防抖
  const generatePersonalizedQuestions = React.useCallback(async () => {
    // 防止重复点击
    if (isGeneratingQuestions || !aiAnalysis) {
      console.log('Already generating or no analysis available');
      return;
    }
    
    console.log('Starting question generation...');
    setIsGeneratingQuestions(true);
    
    try {
      if (DEV_CONFIG.USE_MOCK_AI || !API_CONFIG.DEEPSEEK.API_KEY) {
        // 模拟生成题目
        console.log('Using mock AI for question generation');
        setTimeout(() => {
          const mockQuestions = [
            {
              title: "限时计算：123 × 45 = ?",
              options: ["5535", "5545", "5555", "5565"],
              correct_answer: "5535",
              explanation: "123 × 45 = 123 × (40 + 5) = 4920 + 615 = 5535",
              target_weakness: "计算速度"
            },
            {
              title: "找规律：3, 6, 12, 24, ?",
              options: ["36", "48", "54", "60"],
              correct_answer: "48",
              explanation: "每一项都是前一项的2倍：24 × 2 = 48",
              target_weakness: "模式识别"
            },
            {
              title: "应用题：小华买5本书花了75元，平均每本书多少钱？",
              options: ["12元", "15元", "18元", "20元"],
              correct_answer: "15元",
              explanation: "75 ÷ 5 = 15元",
              target_weakness: "数学应用"
            }
          ];
          
          console.log('Mock questions generated:', mockQuestions);
          setGeneratedQuestions(mockQuestions);
          setIsGeneratingQuestions(false);
        }, 1500);
      } else {
        const deepseekService = new DeepSeekService(API_CONFIG.DEEPSEEK.API_KEY);
        const questions = await deepseekService.generateTargetedQuestions(
          aiAnalysis.weaknesses,
          subject,
          3
        );
        console.log('Real AI questions generated:', questions);
        setGeneratedQuestions(questions);
        setIsGeneratingQuestions(false);
      }
    } catch (error) {
      console.error('生成题目失败:', error);
      setIsGeneratingQuestions(false);
    }
  }, [aiAnalysis, subject, isGeneratingQuestions]); // 依赖项要明确

  // 准备雷达图数据
  const radarData = Object.entries(result.ability_scores).map(([key, value]) => ({
    dimension: dimensionNames[key as keyof typeof dimensionNames],
    score: Math.round(value * 100),
    fullMark: 100
  }));

  const overallScorePercent = Math.round(result.overall_score * 100);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLevel = (score: number) => {
    if (score >= 80) return '优秀';
    if (score >= 60) return '良好';
    return '需提升';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* 标题和总体评分 */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
          <Brain className="h-10 w-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {testMode === 'adaptive' ? 'AI自适应诊断报告' : 'AI智能诊断报告'}
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          {subject === 'math' ? '数学' : '语文'}学习能力
          {testMode === 'adaptive' ? '个性化' : '深度'}分析
        </p>
        {testMode === 'adaptive' && (
          <div className="mb-4">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-sm font-medium rounded-full">
              🚀 本报告基于AI实时生成的{questions.length}道专属题目
            </span>
          </div>
        )}
        <div className="flex items-center justify-center space-x-8">
          <div className="text-center">
            <div className={`text-4xl font-bold ${getScoreColor(overallScorePercent)}`}>
              {overallScorePercent}
            </div>
            <div className="text-sm text-gray-600">综合得分</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-semibold ${getScoreColor(overallScorePercent)}`}>
              {getScoreLevel(overallScorePercent)}
            </div>
            <div className="text-sm text-gray-600">总体水平</div>
          </div>
        </div>
      </div>

      {/* AI深度分析区域 */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
        <div className="flex items-center mb-4">
          <Sparkles className="h-6 w-6 text-purple-600 mr-2" />
          <h3 className="text-xl font-semibold text-gray-900">AI深度分析</h3>
          {isAnalyzing && <Loader className="h-5 w-5 text-purple-600 ml-2 animate-spin" />}
        </div>
        
        {isAnalyzing ? (
          <div className="text-center py-8">
            <Loader className="h-8 w-8 text-purple-600 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">AI正在深度分析您的学习特征...</p>
          </div>
        ) : aiAnalysis ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 学习风格 */}
            <div className="bg-white rounded-lg p-4 border border-purple-100">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                学习风格画像
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed">{aiAnalysis.learningStyle}</p>
            </div>

            {/* 认知特征 */}
            <div className="bg-white rounded-lg p-4 border border-purple-100">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Brain className="h-5 w-5 mr-2 text-purple-600" />
                认知特征分析
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed">{aiAnalysis.cognitiveProfile}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-600">AI分析暂时不可用，显示基础分析结果</p>
          </div>
        )}
      </div>

      {/* 传统雷达图 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Target className="h-6 w-6 mr-2 text-blue-500" />
            能力分布图
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="dimension" className="text-xs" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar
                  name="能力分数"
                  dataKey="score"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 详细分析洞察 */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Zap className="h-6 w-6 mr-2 text-yellow-500" />
            深度洞察
          </h3>
          {aiAnalysis ? (
            <div className="space-y-3">
              {aiAnalysis.detailedInsights.map((insight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{insight}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>等待AI分析完成...</p>
            </div>
          )}
        </div>
      </div>

      {/* 优势与薄弱环节 */}
      {aiAnalysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 优势 */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
              <TrendingUp className="h-6 w-6 mr-2" />
              您的学习优势
            </h3>
            <div className="space-y-2">
              {aiAnalysis.strengths.map((strength, index) => (
                <div key={index} className="bg-white rounded-lg p-3 border border-green-200">
                  <span className="text-green-700 font-medium">{strength}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 薄弱环节 */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-red-800 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 mr-2" />
              需要改进的方面
            </h3>
            <div className="space-y-2">
              {aiAnalysis.weaknesses.map((weakness, index) => (
                <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                  <span className="text-red-700 font-medium">{weakness}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI个性化建议 */}
      {aiAnalysis && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-4 flex items-center">
            <Lightbulb className="h-6 w-6 mr-2" />
            AI个性化学习建议
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-blue-700 mb-3">学习方法建议</h4>
              <div className="space-y-2">
                {aiAnalysis.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="text-blue-800 text-sm leading-relaxed">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-blue-700 mb-3">行动计划</h4>
              <div className="space-y-2">
                {aiAnalysis.nextSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="text-blue-800 text-sm leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 智能题目生成 */}
      {aiAnalysis && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <BookOpen className="h-6 w-6 mr-2 text-purple-500" />
              AI定制练习题
            </h3>
            <button
              onClick={generatePersonalizedQuestions}
              disabled={isGeneratingQuestions}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all ${
                isGeneratingQuestions 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-purple-500 text-white hover:bg-purple-600 active:bg-purple-700'
              }`}
              type="button"
            >
              {isGeneratingQuestions ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  生成中...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  生成专属练习题
                </>
              )}
            </button>
          </div>

          {generatedQuestions.length > 0 && (
            <div className="space-y-4">
              {generatedQuestions.map((question, index) => (
                <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      练习题 {index + 1}
                    </h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      针对：{question.target_weakness}
                    </span>
                  </div>
                  <p className="text-gray-800 mb-3">{question.title}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {question.options.map((option: string, optIdx: number) => (
                      <div key={optIdx} className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                        {String.fromCharCode(65 + optIdx)}. {option}
                      </div>
                    ))}
                  </div>
                  <details className="text-sm">
                    <summary className="text-blue-600 cursor-pointer hover:text-blue-800">
                      查看答案和解析
                    </summary>
                    <div className="mt-2 p-3 bg-blue-50 rounded">
                      <p><strong>正确答案：</strong>{question.correct_answer}</p>
                      <p><strong>解析：</strong>{question.explanation}</p>
                    </div>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 操作按钮 */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={onRestart}
          className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
        >
          重新测试
        </button>
        <button
          onClick={() => window.print()}
          className="px-8 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
        >
          打印报告
        </button>
      </div>

      {/* 免责声明 */}
      <div className="text-center text-xs text-gray-500 mt-8 p-4 bg-gray-50 rounded-lg">
        <p>* 此AI诊断结果仅供参考，基于大模型分析生成。建议结合实际学习情况和专业老师指导制定学习计划。</p>
      </div>
    </div>
  );
};